// src/checklists/index.js
import { isWeeklyLog, logWeekRange, weeklyLogStatus } from '../logs/logFrequency.js';   // v129.96 — ONE weekly-log rule (any day, due Monday, once/week), shared with the logs screen
import { lsSetSafe } from '../storage/quota.js';   // v129.99 — quota self-healing (Dar Al Salam "storage full")
import { saveChecklist, initChecklistOutbox } from './outbox.js';       // v130.39 — offline write queue (Meadows "not synced")
// Checklists — extracted from index.html (v105.x sprint, auto-generated header).
// Bundled by esbuild into dist/checklists.js. The HTML loads it via
//   <script src="dist/checklists.js"></script>
// and call sites use `Checklists.<sub>.<fn>()` (e.g.
//   Checklists.templates.loadChecklistTemplatesFromDB()
//   Checklists.dates.currentChecklistDate()
//   Checklists.screen.renderChecklistDashboard()
//   Checklists.admin.initAdminChecklistView()
// ).
//
// Backwards compat: index.html's trailing script block re-exposes the
// moved functions on window so existing call sites — both inline JS and
// onclick="" attributes — keep working without HTML edits.
//
// Free variables (DB, currentUser, currentStore, escapeHtml, WORKER_URL,
// fetch, console, document, window, ...) live in the inline globals
// scope of index.html. esbuild leaves them as bare references at runtime.

// ── templates ─────────────────────────────────────────────
export const templates = {};

async function loadChecklistTemplatesFromDB() {
  if (typeof DB === 'undefined') return;
  try {
    var rows = await DB.checklistTemplates.list();
    if (Array.isArray(rows)) clTemplates = rows;
    dlog('[DB] Loaded ' + clTemplates.length + ' checklist templates');
  } catch(e) {
    console.warn('[DB] Template load failed:', e.message);
  }
}
templates.loadChecklistTemplatesFromDB = loadChecklistTemplatesFromDB;

function getActiveChecklistsForDate(dateObj) {
  var d = dateObj || new Date();
  var dayAbbr = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
  var dayOfMonth = d.getDate();
  return clTemplates.filter(function(t){
    if (t.active === false) return false;
    var freq = t.frequency || 'daily';
    if (freq === 'daily') return true;
    var cfg = t.frequency_config || {};
    if (freq === 'weekly') {
      // D3: due on the store's single country week-start day (UAE=Mon, Qatar=Sun),
      // not every day the template lists — otherwise it shows due twice a week.
      if (Array.isArray(cfg.days) && cfg.days.length) {
        return (typeof weeklyDueDayForStore === 'function')
          ? (weeklyDueDayForStore(cfg.days) === dayAbbr)
          : (cfg.days.indexOf(dayAbbr) !== -1);
      }
      return (cfg.day || 'Mon') === dayAbbr;
    }
    if (freq === 'monthly') return (cfg.date || 1) === dayOfMonth;
    return false;
  }).sort(function(a,b){
    var oa = a.sort_order || 0, ob = b.sort_order || 0;
    if (oa !== ob) return oa - ob;
    return (a.name || '').localeCompare(b.name || '');
  });
}
templates.getActiveChecklistsForDate = getActiveChecklistsForDate;

function getChecklistTemplate(type){
  // First: DB-loaded templates
  var found = clTemplates.find(function(t){ return t.type === type; });
  if (found && Array.isArray(found.items)) return found.items;
  // Fallback: localStorage override (legacy)
  try{
    var stored=localStorage.getItem('elc_checklist_template_'+type);
    if(stored)return JSON.parse(stored);
  }catch(e){}
  // Last resort: hardcoded defaults
  return DEFAULT_CHECKLISTS[type] || [];
}
templates.getChecklistTemplate = getChecklistTemplate;

function getChecklistName(type) {
  var t = clTemplates.find(function(x){ return x.type === type; });
  if (t && t.name) return t.name;
  if (type === 'open')  return 'Opening Checklist';
  if (type === 'close') return 'Closing Checklist';
  return type;
}
templates.getChecklistName = getChecklistName;

// ── dates ─────────────────────────────────────────────
export const dates = {};

function currentChecklistDate() {
  var inp = document.getElementById('clHistoryDate');
  return (inp && inp.value) ? inp.value : currentTradingDate();
}
dates.currentChecklistDate = currentChecklistDate;

function shiftChecklistDate(days) {
  var inp = document.getElementById('clHistoryDate');
  if (!inp) return;
  var base = inp.value ? new Date(inp.value + 'T12:00:00') : new Date();
  var d = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
  // v79.19: cap forward navigation at the current TRADING day, not calendar
  // today. At 00:30 Tuesday, trading=Monday — user shouldn't be able to
  // tab forward into Tuesday's view (where they'd see Tuesday's pending
  // close, which doesn't actually need filing yet).
  var tradingToday = new Date(currentTradingDate() + 'T12:00:00');
  if (d > tradingToday) { showToast('Already at today','info'); return; }
  var str = localDateStr(d);
  var tradingTodayStr = currentTradingDate();
  inp.value = (str === tradingTodayStr) ? '' : str;
  onClHistoryDateChange();
}
dates.shiftChecklistDate = shiftChecklistDate;

function resetChecklistDate() {
  var inp = document.getElementById('clHistoryDate');
  if (inp) inp.value = '';
  onClHistoryDateChange();
}
dates.resetChecklistDate = resetChecklistDate;

// v79.19 — trading-day rollover.
// Bug it fixes: closing checklists submitted between 00:00 and ~04:00 were
// recorded against the calendar date the staff hit Submit (the NEW day),
// not the trading day they had just closed. A store manager reported this on
// pilot day 1 — Monday's closing checklist filed under Tuesday.
//
// The fix is a single concept: from 00:00 to TRADING_DAY_ROLLOVER_HOUR
// local time, the "trading day" is yesterday. After the rollover, it's
// today. Stores close at 22:00 (weekdays) or 24:00 (weekends), so a 04:00
// cutoff gives 4-6 hours of grace to file the closing checklist before
// the day flips. Anything later than 04:00 means the staff is filing
// for the NEW trading day (e.g. opening checks for the morning shift).
//
// This is used in three places:
//   1. currentChecklistDate() — dashboard defaults to the trading day.
//   2. isViewingToday() — matches against trading day.
//   3. submitChecklist() — records under the trading day.
// All three keep the same calendar-date semantics from 04:00 onward,
// so opening checklists, daily opens, weekly Mondays etc. are unaffected.
//
// Note: actualSubmittedOn (line ~3697) still uses todayDateStr() — that
// field is the audit trail of WHEN the form was actually filed, which
// is calendar-correct.
var TRADING_DAY_ROLLOVER_HOUR = 4;
function currentTradingDate() {
  var now = new Date();
  if (now.getHours() < TRADING_DAY_ROLLOVER_HOUR) {
    var yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return localDateStr(yesterday);
  }
  return localDateStr(now);
}
dates.currentTradingDate = currentTradingDate;

// Which date is currently being VIEWED in the checklist screen.
// Returns YYYY-MM-DD. Empty input → the current TRADING day (yesterday's
// calendar date if we're in the post-midnight rollover window, today
// otherwise — see currentTradingDate above). Any explicit value → that
// date verbatim (read-only history view).
// v105.x: currentChecklistDate → Checklists.dates.currentChecklistDate
function isViewingToday() {
  var inp = document.getElementById('clHistoryDate');
  // Empty input means default-trading-date view → "today" for UX purposes.
  // Explicit value matches against the trading date, not the calendar date,
  // so the submit bar shows when the user is on the trading-day view.
  return !inp || !inp.value || inp.value === currentTradingDate();
}
dates.isViewingToday = isViewingToday;
// v105.x: shiftChecklistDate → Checklists.dates.shiftChecklistDate
// v105.x: resetChecklistDate → Checklists.dates.resetChecklistDate
function onClHistoryDateChange() {
  // v136.5: a day picked in the calendar that IS today (or later) is stored as '' (= the trading day), like the
  // ‹ › buttons do, so it cannot go stale overnight and hide Submit the next day (the Safe Log bug, 16 Oct).
  var inp = document.getElementById('clHistoryDate');
  if (inp && inp.value && inp.value >= currentTradingDate()) inp.value = '';
  // When user changes the date picker, re-render whichever view is active.
  // Dashboard shows a per-day summary; drill-down re-renders its content for the viewed date.
  if (clView === 'checklist') {
    renderChecklistScreen();
  } else {
    renderChecklistDashboard();
  }
}
dates.onClHistoryDateChange = onClHistoryDateChange;

// ── helpers ─────────────────────────────────────────────
export const helpers = {};

// v130.38 — a LOCAL record only counts as a submission once the server has it.
// The local copy is written before the DB call so answers survive a failure;
// without this gate the dashboard read that copy back as "✅ Submitted" and
// disagreed with compliance, which was correctly reporting nothing on file.
//
// Records written before this flag existed have no `synced` field. Those are
// treated as synced deliberately — otherwise every historical checklist on
// every device would re-open as outstanding the moment this ships.
function clLocalSubmitted(raw){
  if(!raw) return null;
  var rec=null;
  try{ rec = typeof raw === 'string' ? JSON.parse(raw) : raw; }catch(e){ return null; }
  if(!rec) return null;
  return rec.synced === false ? null : rec;
}
helpers.clLocalSubmitted = clLocalSubmitted;

// v130.39 — the outbox calls this once a queued checklist finally reaches the
// server, so the device's own copy stops reading as outstanding. Pure local
// bookkeeping: the server already has the record either way.
function markLocalSynced(store, date, type){
  try{
    var key = clStorageKey(store, date, type);
    var rec = JSON.parse(localStorage.getItem(key) || 'null');
    if (!rec) return false;
    rec.synced = true;
    lsSetSafe(key, JSON.stringify(rec));
    return true;
  }catch(e){ return false; }
}
helpers.markLocalSynced = markLocalSynced;

function clStorageKey(store,date,type){
  return 'elc_cl_'+store.replace(/\s+/g,'_')+'_'+date+'_'+type;
}
helpers.clStorageKey = clStorageKey;

// Pick an emoji for a template based on its name (matches renderChecklistTabs)
function templateEmoji(name) {
  var nameLc = (name || '').toLowerCase();
  if (nameLc.indexOf('open') > -1) return '☀️';
  if (nameLc.indexOf('clos') > -1) return '🌙';
  if (nameLc.indexOf('sales') > -1) return '💰';
  if (nameLc.indexOf('stock') > -1 || nameLc.indexOf('invent') > -1) return '📦';
  if (nameLc.indexOf('clean') > -1) return '🧹';
  return '✅';
}
helpers.templateEmoji = templateEmoji;

// Was this template active on a given date, according to its frequency?
// v55 bugfix: support cfg.days[] for weekly templates, matching getActiveChecklistsForDate.
function templateShouldAppearOn(tmpl, dateObj) {
  if (!tmpl || tmpl.active === false) return false;
  var freq = tmpl.frequency || 'daily';
  if (freq === 'daily') return true;
  var cfg = tmpl.frequency_config || {};
  var dayAbbr = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dateObj.getDay()];
  if (freq === 'weekly') {
    // D3: due on the store's single country week-start day, not every listed day.
    if (Array.isArray(cfg.days) && cfg.days.length) return weeklyDueDayForStore(cfg.days) === dayAbbr;
    return (cfg.day || 'Mon') === dayAbbr;
  }
  if (freq === 'monthly') return (cfg.date || 1) === dateObj.getDate();
  return false;
}
helpers.templateShouldAppearOn = templateShouldAppearOn;

// v56: Late-submission support. For weekly/monthly templates, within LATE_GRACE_DAYS
// of the original scheduled date, a missed submission can still be filed — flagged as
// `submitted_late` with the original due date preserved. Daily templates are excluded:
// opening/closing checks are time-of-day specific and late submission defeats the purpose.
var LATE_GRACE_DAYS = 2;

// For a given template and "today", return the most recent scheduled date within the
// grace window that doesn't have a submission yet. Null if no eligible late-submit slot.
// Async because it reads DB submissions; falls back to localStorage.
async function findLateEligibleDueDate(tmpl, todayObj, store) {
  if (!tmpl || tmpl.active === false) return null;
  var freq = tmpl.frequency || 'daily';
  if (freq === 'daily') return null; // daily checklists cannot be submitted late
  // Walk back 1..LATE_GRACE_DAYS. Return the most recent scheduled day in that window
  // that has no existing submission.
  for (var d = 1; d <= LATE_GRACE_DAYS; d++) {
    var past = new Date(todayObj); past.setDate(past.getDate() - d);
    if (!templateShouldAppearOn(tmpl, past)) continue;
    var dateStr = localDateStr(past);
    // Check DB first, then localStorage
    var submitted = false;
    if (typeof DB !== 'undefined') {
      try {
        var rows = await DB.checklists.list({ store: store, from: dateStr, to: dateStr });
        if (Array.isArray(rows) && rows.some(function(r){ return r.type === tmpl.type; })) submitted = true;
      } catch(e) { /* fall through to local */ }
    }
    if (!submitted) {
      try {
        var k = clStorageKey(store, dateStr, tmpl.type);
        if (clLocalSubmitted(localStorage.getItem(k))) submitted = true;
      } catch(e) {}
    }
    if (!submitted) return { dueDate: dateStr, dueDateObj: past };
  }
  return null;
}
helpers.findLateEligibleDueDate = findLateEligibleDueDate;

// ── progress (partial-checklist state) ─────────────────────────────────────────────
export const progress = {};

// v65: PARTIAL-CHECKLIST PROGRESS — keyed by (store, date, type) so a part-way
// checklist on Tuesday doesn't pollute Wednesday's run. Saved automatically
// after every answer (debounced); restored when the user re-opens the same
// checklist. Cleared after successful Submit. Distinct from the SUBMITTED
// record (which uses clStorageKey above) — partials live under a "_partial"
// suffix so the existing dashboard logic that looks up clStorageKey for
// "is this checklist done today" stays untouched.
function clPartialKey(store, date, type){
  return clStorageKey(store, date, type) + '_partial';
}
progress.clPartialKey = clPartialKey;
var _clPartialSaveTimer = null;
function saveClPartialState(){
  // Only save if we've actually started — empty answers means "abandon", not "save"
  if (!clAnswers || Object.keys(clAnswers).length === 0) return;
  var partialDate = clLateFor || todayDateStr();
  var key = clPartialKey(currentStore, partialDate, clType);
  try {
    localStorage.setItem(key, JSON.stringify({
      answers: clAnswers,
      staffId: clStaffId || null,
      savedAt: new Date().toISOString(),
    }));
  } catch(e) {
    // Storage full or unavailable — non-fatal, just stop saving
    console.warn('[Checklist] Partial save failed:', e && e.message);
  }
}
progress.saveClPartialState = saveClPartialState;
function loadClPartialState(){
  var partialDate = clLateFor || todayDateStr();
  var key = clPartialKey(currentStore, partialDate, clType);
  try {
    var raw = localStorage.getItem(key);
    if (!raw) return null;
    var p = JSON.parse(raw);
    return p && p.answers ? p : null;
  } catch(e) { return null; }
}
progress.loadClPartialState = loadClPartialState;
function clearClPartialState(){
  // Clear partials for ALL dates of this checklist type at this store
  // (avoids stale yesterday-partials accumulating). We iterate localStorage
  // because we don't track which dates have partials.
  try {
    var prefix = clStorageKey(currentStore, '', clType).replace(/__$/, '_');
    var toRemove = [];
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      if (k && k.indexOf(prefix) === 0 && k.indexOf('_partial') > 0) toRemove.push(k);
    }
    toRemove.forEach(function(k){ localStorage.removeItem(k); });
  } catch(e) {}
}
progress.clearClPartialState = clearClPartialState;
function debouncedSaveClPartial(){
  if (_clPartialSaveTimer) clearTimeout(_clPartialSaveTimer);
  _clPartialSaveTimer = setTimeout(saveClPartialState, 600);
}
progress.debouncedSaveClPartial = debouncedSaveClPartial;

function updateClProgress(){
  var tmpl=getChecklistTemplate(clType);
  var total=0;tmpl.forEach(function(s){total+=s.items.length;});
  var answered=Object.keys(clAnswers).length;
  var yesCount=Object.values(clAnswers).filter(function(a){return a.answer==='yes';}).length;
  var progressLine = answered+'/'+total+' answered · '+yesCount+' Yes';
  // v65: subtle reassurance that partial progress is safe
  if (answered > 0 && answered < total) progressLine += ' · saved';
  document.getElementById('clProgress').textContent = progressLine;
  var allDone=answered===total&&clStaffId;
  document.getElementById('clSubmitBtn').disabled=!allDone;
  // v65: persist partial progress so a mid-shift handover doesn't lose work
  debouncedSaveClPartial();
}
progress.updateClProgress = updateClProgress;

// ── screen ─────────────────────────────────────────────
export const screen = {};

// ── v125.13: Daily-logs live panel — logs sit SIDE BY SIDE with checks (Matt:
// "I'm worried daily logs are now hidden and they have to do them daily").
// Self-sufficient via DB (templates + entries for the viewed date) — no calls
// into the logs bundle; tolerant: any failure falls back to a plain open link.
async function renderClLogsPanel() {
  var panel = document.getElementById('clLogsPanel');
  if (!panel || typeof DB === 'undefined' || !currentStore) return;
  var dateStr = currentChecklistDate();
  var viewingToday = isViewingToday();
  panel.innerHTML = '<div style="color:var(--sub);font-size:12px;padding:8px 12px">Loading logs…</div>';
  try {
    // v129.96 (Dubai Mall 2's bug + Matt's 9 Aug ruling): fetch the whole
    // Mon-Sun week of the viewed date in the SAME single query — daily rows
    // keep their day counts (entry_date === dateStr); weekly types (any day,
    // due Monday, once per week) are judged against the week. The card and
    // the logs screen now share one rule (src/logs/logFrequency.js) so they
    // can never disagree again.
    var wk = logWeekRange(dateStr);
    var res = await Promise.all([
      DB.logTemplates.list(),
      DB.logs.list({ store: currentStore, dateFrom: wk.from, dateTo: wk.to }),
    ]);
    var tmpls = (Array.isArray(res[0]) ? res[0] : []).filter(function (t) { return t && t.active !== false; })
      .sort(function (a, b) { return (a.sort_order || 0) - (b.sort_order || 0) || (a.name || '').localeCompare(b.name || ''); });
    if (!tmpls.length) { panel.innerHTML = '<div style="color:var(--sub);font-size:12px;padding:8px 12px">No log types set up yet.</div>'; return; }
    var byType = {};
    var weekByType = {};
    (Array.isArray(res[1]) ? res[1] : []).forEach(function (r) {
      if (!r || !r.log_type) return;
      weekByType[r.log_type] = (weekByType[r.log_type] || 0) + 1;
      if (String(r.entry_date) === dateStr) byType[r.log_type] = (byType[r.log_type] || 0) + 1;
    });
    var done = 0;
    var rows = tmpls.map(function (t) {
      var n = byType[t.type] || 0;
      var status;
      if (isWeeklyLog(t)) {
        var ws = weeklyLogStatus(dateStr, weekByType[t.type] || 0);
        if (ws.state === 'done') done++;
        var wsColor = ws.state === 'done' ? 'var(--ok-deep)' : (ws.state === 'due' ? 'var(--ui-warn)' : 'var(--danger-deep)');
        status = '<span style="color:' + wsColor + ';font-weight:700;font-size:12px;white-space:nowrap">' + ws.label + '</span>';
      } else {
        if (n > 0) done++;
        status = n > 0
          ? '<span style="color:var(--ok-deep);font-weight:700;font-size:12px;white-space:nowrap">🟢 ' + n + ' entr' + (n === 1 ? 'y' : 'ies') + '</span>'
          : (viewingToday
            ? '<span style="color:var(--ui-warn);font-weight:700;font-size:12px;white-space:nowrap">⚠️ Not done yet</span>'
            : '<span style="color:var(--sub);font-weight:700;font-size:12px;white-space:nowrap">⚪ None</span>');
      }
      return '<div onclick="switchScreen(\'logs\')" style="display:flex;align-items:center;gap:8px;padding:9px 12px;background:var(--card);border:1px solid var(--border);border-radius:10px;margin-bottom:6px;cursor:pointer">' +
        '<span style="font-size:16px;flex-shrink:0">' + (t.icon || '📝') + '</span>' +
        '<span style="font-weight:600;font-size:13px;color:var(--text);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(t.name || t.type) + '</span>' +
        status + '</div>';
    }).join('');
    var allDone = done === tmpls.length;
    var head = '<div style="display:flex;align-items:center;gap:6px;margin:0 0 8px;padding:0 2px">' +
      '<span style="font-size:15px">📒</span>' +
      '<span style="font-weight:700;font-size:13px;color:var(--text)">Daily logs</span>' +
      '<span style="font-size:11px;font-weight:700;color:' + (allDone ? 'var(--ok-deep)' : 'var(--ui-warn)') + '">' + done + '/' + tmpls.length + ' done</span>' +
      '<span onclick="switchScreen(\'logs\')" style="margin-inline-start:auto;font-size:12px;color:var(--rota-green-ink);font-weight:700;cursor:pointer">Open →</span></div>';
    panel.innerHTML = head + rows;
  } catch (e) {
    panel.innerHTML = '<div onclick="switchScreen(\'logs\')" style="color:var(--sub);font-size:12px;padding:8px 12px;cursor:pointer">📒 Daily logs — open →</div>';
  }
}
screen.renderClLogsPanel = renderClLogsPanel;

async function renderChecklistDashboard() {
  clView = 'dashboard';
  // v130.39 — wire the offline replay triggers and drain anything waiting.
  // Same shape as Clock.initOutbox() on the clock screen: idempotent, so
  // calling it on every render is free and guarantees a store that reopens
  // the app on a good signal sends whatever it queued on a bad one.
  try { initChecklistOutbox(); } catch (e) { /* never block the screen */ }
  var dash = document.getElementById('clDashboard');
  var body = document.getElementById('clBody');
  var submitBar = document.getElementById('clSubmitBar');
  var backBar = document.getElementById('clBackBar');
  var dateNav = document.getElementById('clDateNav');

  // Show dashboard, hide drill-down + submit bar + back bar
  if (dash) dash.style.display = '';
  if (body) body.style.display = 'none';
  if (submitBar) submitBar.style.display = 'none';
  if (backBar) backBar.style.display = 'none';
  if (dateNav) dateNav.style.display = 'flex';

  var dateStr = currentChecklistDate();
  var viewingToday = isViewingToday();
  var viewDate = new Date(dateStr + 'T12:00:00');
  var store = currentStore;

  // v125.13: the Daily-logs live panel renders alongside (fire-and-forget —
  // a logs fetch failure must never delay or break the checks dashboard).
  renderClLogsPanel();

  // Header
  document.getElementById('clHeaderTitle').textContent = store.replace('ELC - ','').split(' - ').pop() + ' — Checks';
  document.getElementById('clHeaderDate').textContent = viewDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) + (viewingToday ? '' : ' (past)');

  // Which checks were/are scheduled for the viewed date?
  var scheduled = getActiveChecklistsForDate(viewDate);

  // v56: When viewing today, also look for weekly/monthly checks that were DUE in the
  // past 2 days and never submitted — those are eligible for late submission.
  var lateEligible = [];
  if (viewingToday && Array.isArray(clTemplates)) {
    for (var i = 0; i < clTemplates.length; i++) {
      var t = clTemplates[i];
      if (t.active === false) continue;
      // Skip daily templates and anything already in today's scheduled list
      if ((t.frequency || 'daily') === 'daily') continue;
      if (scheduled.some(function(s){ return s.type === t.type; })) continue;
      var slot = await findLateEligibleDueDate(t, viewDate, store);
      if (slot) lateEligible.push({ tmpl: t, dueDate: slot.dueDate, dueDateObj: slot.dueDateObj });
    }
  }

  if (!dash) return;
  dash.innerHTML = '';

  if (!scheduled.length && !lateEligible.length) {
    dash.innerHTML = '<div style="text-align:center;color:var(--sub);padding:40px 20px;font-size:13px">No checks scheduled for this day.</div>';
    return;
  }

  // v136.8 (accessibility audit): a placeholder in the shape of what is coming (shift-ui.css .ui-skel), not "Loading..."
  dash.innerHTML = '<div aria-busy="true" aria-label="Loading checks" style="padding:4px 0 12px">' +
    '<span class="ui-skel" style="height:58px;margin:0 0 12px"></span>' +
    '<span class="ui-skel" style="height:64px;margin:0 0 8px"></span>' +
    '<span class="ui-skel" style="height:64px;margin:0 0 8px"></span></div>';

  // Fetch DB submissions for this store + date in one go, keyed by type
  var dbByType = {};
  var dbReadFailed = false;   // v136.8: the statuses below then come from this device's own copy, and we say so
  if (typeof DB !== 'undefined') {
    try {
      var rows = await DB.checklists.list({ store: store, from: dateStr, to: dateStr });
      (Array.isArray(rows) ? rows : []).forEach(function(r){
        if (r.type) dbByType[r.type] = r;
      });
    } catch(e) {
      dbReadFailed = true;
      console.warn('[DB] Checklist dashboard load failed:', e.message);
    }
  }

  // Compute status for each scheduled check — DB first, localStorage as fallback/offline cache
  var submissions = scheduled.map(function(t){
    var sub = null;
    var dbRow = dbByType[t.type];
    if (dbRow) {
      var items = dbRow.items || {};
      sub = {
        staffName: dbRow.completed_by || items.staff_name || '—',
        submittedAt: items.submitted_at || (dbRow.completed_at ? String(dbRow.completed_at).slice(11,16) : '—'),
        score: items.score != null ? items.score : (items.answers ? Object.values(items.answers).filter(function(a){return a && a.answer==='yes';}).length : 0),
        total: items.total || (items.answers ? Object.keys(items.answers).length : 0),
      };
    } else {
      // Local fallback (works offline, or if DB write failed silently)
      var key = clStorageKey(store, dateStr, t.type);
      try { sub = clLocalSubmitted(localStorage.getItem(key)); } catch(e){}
    }
    return { tmpl: t, sub: sub };
  });
  var doneCount = submissions.filter(function(s){ return s.sub; }).length;
  var totalCount = submissions.length;
  var pct = totalCount ? Math.round(doneCount / totalCount * 100) : 0;

  dash.innerHTML = '';

  // v136.8 (accessibility audit): the server read failed, so what follows is this device's own copy. Say so in one
  // line, with a way to try again, instead of showing possibly stale ticks as if they were the store's record.
  if (dbReadFailed) {
    var stale = document.createElement('div');
    stale.setAttribute('role', 'status');
    stale.style.cssText = 'display:flex;align-items:center;gap:10px;background:var(--warn-bg);color:var(--warn-deep);border-radius:12px;padding:8px 12px;margin-bottom:10px;font-size:13px;font-weight:600';
    stale.innerHTML = '<span style="flex:1">Couldn\'t reach Shift, statuses may be out of date</span>' +
      '<button type="button" onclick="Checklists.screen.renderChecklistDashboard()" style="min-height:44px;padding:0 14px;border-radius:10px;border:1.5px solid currentColor;background:transparent;color:inherit;font:inherit;font-weight:700;cursor:pointer">Retry</button>';
    dash.appendChild(stale);
  }

  // Progress summary card
  var progWrap = document.createElement('div');
  progWrap.style.cssText = 'background:var(--card);border-radius:14px;padding:14px 16px;margin-bottom:12px;box-shadow:0 1px 3px rgba(0,0,0,.06)';
  var allDone = doneCount === totalCount && totalCount > 0;
  if (allDone && viewingToday) {
    progWrap.style.cssText += ';background:linear-gradient(135deg,var(--ok-bg),var(--stores-ok-line));border:1.5px solid var(--ok-border)';
    progWrap.innerHTML =
      '<div style="font-size:16px;font-weight:800;color:var(--ok-deep);margin-bottom:2px">🎉 All checks complete!</div>' +
      '<div style="font-size:12px;color:var(--ok-deep);opacity:.85">Great work, team.</div>';
  } else {
    var lbl = viewingToday
      ? doneCount + ' of ' + totalCount + ' checks complete'
      : doneCount + ' of ' + totalCount + ' completed on this day';
    progWrap.innerHTML =
      '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">' +
        '<div style="font-size:13px;font-weight:700;color:var(--text)">' + lbl + '</div>' +
        '<div style="font-size:13px;font-weight:800;color:var(--rota-green-ink)">' + pct + '%</div>' +
      '</div>' +
      '<div style="height:8px;background:var(--border);border-radius:6px;overflow:hidden">' +
        '<div style="height:100%;width:' + pct + '%;background:var(--green);border-radius:6px;transition:width .2s"></div>' +
      '</div>';
  }
  dash.appendChild(progWrap);

  // One card per scheduled check
  submissions.forEach(function(row){
    var t = row.tmpl;
    var sub = row.sub;
    var emoji = templateEmoji(t.name);
    var name = t.name || t.type;

    var card = document.createElement('div');
    card.style.cssText = 'background:var(--card);border-radius:14px;padding:14px 16px;margin-bottom:10px;box-shadow:0 1px 3px rgba(0,0,0,.06);cursor:pointer;transition:transform .1s,box-shadow .1s';
    card.addEventListener('mousedown', function(){ card.style.transform = 'scale(.99)'; });
    card.addEventListener('mouseup',    function(){ card.style.transform = ''; });
    card.addEventListener('mouseleave', function(){ card.style.transform = ''; });
    card.addEventListener('click', (function(type){ return function(){ switchChecklist(type); }; })(t.type));

    var statusLine;
    if (sub) {
      var pctRow = Math.round((sub.score || 0) / (sub.total || 1) * 100);
      var col = pctRow === 100 ? 'var(--ok-deep)' : pctRow >= 80 ? 'var(--warn-deep)' : 'var(--danger-deep)';
      var ind = pctRow === 100 ? '🟢' : pctRow >= 80 ? '🟡' : '🔴';
      statusLine =
        '<div style="font-size:12px;color:' + col + ';font-weight:700">' + ind + ' Submitted ' + (sub.submittedAt || '') + ' by ' + escapeHtml(sub.staffName || '') + '</div>' +
        '<div style="font-size:11px;color:var(--sub);margin-top:2px">' + (sub.score || 0) + '/' + (sub.total || 0) + ' items · ' + pctRow + '%</div>';
    } else if (viewingToday) {
      var freqLabel = (function(){
        var f = t.frequency || 'daily';
        if (f === 'daily')   return 'Daily';
        if (f === 'weekly')  return 'Weekly';
        if (f === 'monthly') return 'Monthly';
        return f;
      })();
      statusLine = '<div style="font-size:12px;color:var(--sub)">⚪ Not started · ' + freqLabel + '</div>';
    } else {
      // Past date, was scheduled, not submitted → missed
      statusLine = '<div style="font-size:12px;color:var(--danger-deep);font-weight:700">❌ Not submitted</div>';
    }

    card.innerHTML =
      '<div style="display:flex;align-items:center;gap:10px">' +
        '<div style="font-size:22px;flex-shrink:0">' + emoji + '</div>' +
        '<div style="flex:1;min-width:0">' +
          '<div style="font-size:14px;font-weight:700;color:var(--text);margin-bottom:3px">' + escapeHtml(name) + '</div>' +
          statusLine +
        '</div>' +
        '<div style="font-size:18px;color:var(--sub);flex-shrink:0">›</div>' +
      '</div>';
    dash.appendChild(card);
  });

  // v56: Render late-eligible checks (weekly/monthly missed within 2-day grace window)
  // Visually distinct amber cards — clicking opens the checklist with late-submission context.
  if (lateEligible.length) {
    var hdr = document.createElement('div');
    hdr.style.cssText = 'font-size:11px;font-weight:700;color:var(--warn-deep);text-transform:uppercase;letter-spacing:.5px;margin:16px 0 8px;padding-inline-start:4px';
    hdr.textContent = '⚠ Late — submit now';
    dash.appendChild(hdr);

    lateEligible.forEach(function(slot){
      var t = slot.tmpl;
      var dueLabel = slot.dueDateObj.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' });
      var emoji = templateEmoji(t.name);
      var name = t.name || t.type;

      var card = document.createElement('div');
      card.style.cssText = 'background:var(--warn-bg);border:1.5px solid var(--stores-warn-line);border-radius:14px;padding:14px 16px;margin-bottom:10px;cursor:pointer;transition:transform .1s';
      card.addEventListener('mousedown', function(){ card.style.transform = 'scale(.99)'; });
      card.addEventListener('mouseup',    function(){ card.style.transform = ''; });
      card.addEventListener('mouseleave', function(){ card.style.transform = ''; });
      card.addEventListener('click', (function(type, due){
        return function(){ switchChecklistLate(type, due); };
      })(t.type, slot.dueDate));

      card.innerHTML =
        '<div style="display:flex;align-items:center;gap:10px">' +
          '<div style="font-size:22px;flex-shrink:0">' + emoji + '</div>' +
          '<div style="flex:1;min-width:0">' +
            '<div style="font-size:14px;font-weight:700;color:var(--warn-deep);margin-bottom:3px">' + escapeHtml(name) + '</div>' +
            '<div style="font-size:12px;color:var(--warn-deep);font-weight:600">Was due ' + dueLabel + '</div>' +
            '<div style="font-size:11px;color:var(--sub);margin-top:2px">Within 2-day grace window · will be marked as late</div>' +
          '</div>' +
          '<div style="font-size:18px;color:var(--warn-deep);flex-shrink:0">›</div>' +
        '</div>';
      dash.appendChild(card);
    });
  }
}
screen.renderChecklistDashboard = renderChecklistDashboard;

function switchChecklist(type){
  clType = type;
  clView = 'checklist';
  clAnswers = {};
  clLateFor = null; // v56: clear any lingering late-submission context

  // v65: restore partial-progress state if the user was part-way through
  // earlier in the day. Only restores answers — staff picker stays empty
  // so they confirm their identity before submit.
  var partial = loadClPartialState();
  if (partial && partial.answers) {
    clAnswers = partial.answers;
    if (partial.staffId) clStaffId = partial.staffId;
    showToast('Restored ' + Object.keys(clAnswers).length + ' previously answered item(s)', 'info');
  }

  var dash = document.getElementById('clDashboard');
  var body = document.getElementById('clBody');
  var backBar = document.getElementById('clBackBar');
  var dateNav = document.getElementById('clDateNav');

  if (dash) dash.style.display = 'none';
  if (body) body.style.display = '';
  if (backBar) backBar.style.display = '';
  if (dateNav) dateNav.style.display = 'none'; // hide date nav inside drill-down; nav from dashboard only

  renderChecklistScreen();
}
screen.switchChecklist = switchChecklist;

function switchChecklistLate(type, dueDate) {
  clType = type;
  clView = 'checklist';
  clAnswers = {};
  clLateFor = dueDate; // YYYY-MM-DD of the missed scheduled day

  // v65: restore partial-progress for the LATE-FOR date (not today)
  var partial = loadClPartialState();
  if (partial && partial.answers) {
    clAnswers = partial.answers;
    if (partial.staffId) clStaffId = partial.staffId;
    showToast('Restored ' + Object.keys(clAnswers).length + ' previously answered item(s)', 'info');
  }

  var dash = document.getElementById('clDashboard');
  var body = document.getElementById('clBody');
  var backBar = document.getElementById('clBackBar');
  var dateNav = document.getElementById('clDateNav');

  if (dash) dash.style.display = 'none';
  if (body) body.style.display = '';
  if (backBar) backBar.style.display = '';
  if (dateNav) dateNav.style.display = 'none';

  renderChecklistScreen();
}
screen.switchChecklistLate = switchChecklistLate;

function backToChecklistDashboard(){
  renderChecklistDashboard();
}
screen.backToChecklistDashboard = backToChecklistDashboard;

async function renderChecklistScreen(){
  var dateStr = currentChecklistDate();
  var viewingToday = isViewingToday();
  var viewDate = new Date(dateStr + 'T12:00:00');
  var store=currentStore;
  document.getElementById('clHeaderDate').textContent = viewDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) + (viewingToday ? '' : ' (viewing history)');
  document.getElementById('clHeaderTitle').textContent=store.replace('ELC - ','').split(' - ').pop()+' — '+getChecklistName(clType);

  var body=document.getElementById('clBody');
  body.innerHTML='';

  var submitBar = document.getElementById('clSubmitBar');
  // Show submit bar when viewing today OR when filing a late submission.
  // Hide when viewing past history.
  if (submitBar) submitBar.style.display = (viewingToday || clLateFor) ? 'block' : 'none';

  // v56: amber banner for late-submission mode
  if (clLateFor) {
    var dueObj = new Date(clLateFor + 'T12:00:00');
    var dueLabel = dueObj.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'});
    var lateBanner = document.createElement('div');
    lateBanner.style.cssText = 'background:var(--warn-bg);border:1.5px solid var(--stores-warn-line);border-radius:12px;padding:12px 14px;margin:12px;color:var(--warn-deep);font-size:13px;line-height:1.5';
    lateBanner.innerHTML =
      '<div style="font-weight:700;margin-bottom:4px">⚠ Late submission for ' + dueLabel + '</div>' +
      '<div style="font-size:12px;opacity:.85">This will be recorded as submitted late. Please add a reason in any item comments if relevant.</div>';
    body.appendChild(lateBanner);
  }

  // Check for an existing submission on the viewed date (or on the late-for date if filing late).
  //
  // v86.16: DB-first check (mirrors the pattern at line ~3979-3997's late-find path).
  // localStorage is per-device — if a colleague submitted from their phone, this
  // device has no localStorage entry for it, and we'd render the form for re-fill.
  // The user fills out every item, presses submit, and hits the DB UNIQUE constraint
  // on (store, date, type). By checking the DB first, cross-device submissions render
  // the submitted banner directly. Falls back to localStorage when DB is unavailable
  // (offline or worker down). (Bug reported by a staff member, Dubai - Wafi City, May 14 2026.)
  var storageDate = clLateFor || dateStr;
  var key=clStorageKey(store,storageDate,clType);
  var existing=null;
  if (typeof DB !== 'undefined' && DB.checklists && DB.checklists.list) {
    try {
      var rows = await DB.checklists.list({ store: store, from: storageDate, to: storageDate });
      var dbRow = (Array.isArray(rows) ? rows : []).find(function(r){ return r.type === clType; });
      if (dbRow && dbRow.items) {
        // Reconstruct the same shape the localStorage record has, so the existing-submission
        // rendering branch below treats either source identically.
        existing = {
          store: dbRow.store, date: dbRow.date, type: dbRow.type,
          staffId: dbRow.items.staff_id, staffName: dbRow.items.staff_name,
          submittedAt: dbRow.items.submitted_at,
          answers: dbRow.items.answers,
          score: dbRow.items.score, total: dbRow.items.total,
          submittedLate: dbRow.items.submitted_late,
          lateForDate: dbRow.items.late_for_date,
          actualSubmittedOn: dbRow.items.actual_submitted_on,
          // v126.75 — a Head Office WAIVER row (e.g. the 21-Jul outage: inserted
          // by SQL with items={waived,note} and completed_by, no answers).
          waived: !!dbRow.items.waived,
          waivedNote: dbRow.items.note || null,
          completedBy: dbRow.completed_by || null,
        };
      }
    } catch(e) { /* DB unavailable — fall through to local */ }
  }
  if (!existing) {
    try { existing=clLocalSubmitted(localStorage.getItem(key)); } catch(e) {}
  }

  // When viewing a past date with no submission, check whether the template was scheduled for that date.
  // For daily: always expected. For weekly/monthly: only expected on matching days.
  if (!viewingToday && !clLateFor && !existing) {
    var tmplObj = (clTemplates || []).find(function(t){ return t.type === clType; });
    var wasScheduled = tmplObj ? templateShouldAppearOn(tmplObj, viewDate) : true;
    var msg;
    if (!tmplObj) {
      msg = 'This checklist template no longer exists, so there is no submission for ' + viewDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) + '.';
    } else if (!wasScheduled) {
      var freqLabel = (function(){
        var f = tmplObj.frequency || 'daily';
        var c = tmplObj.frequency_config || {};
        if (f === 'weekly') {
          // v55: support days[] array alongside legacy day string
          var DAY_FULL = {Mon:'Mondays',Tue:'Tuesdays',Wed:'Wednesdays',Thu:'Thursdays',Fri:'Fridays',Sat:'Saturdays',Sun:'Sundays'};
          if (Array.isArray(c.days) && c.days.length) {
            var order = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
            var picked = order.filter(function(d){ return c.days.indexOf(d) !== -1; }).map(function(d){ return DAY_FULL[d]; });
            return 'Scheduled weekly on ' + picked.join(' & ');
          }
          var d = c.day || 'Mon';
          return 'Scheduled weekly on ' + (DAY_FULL[d] || d);
        }
        if (f === 'monthly') return 'Scheduled monthly on day ' + (c.date || 1);
        return 'Scheduled daily';
      })();
      msg = freqLabel + ' — not due on ' + viewDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) + '.';
    } else {
      msg = 'This checklist was not submitted on ' + viewDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) + '.';
    }
    var miss = document.createElement('div');
    miss.style.cssText = 'padding:24px;text-align:center;background:var(--card);border-radius:12px;margin:12px;box-shadow:0 1px 3px rgba(0,0,0,.05)';
    miss.innerHTML = '<div style="font-size:32px;margin-bottom:8px">📭</div><div style="font-size:14px;color:var(--sub)">' + msg + '</div>';
    body.appendChild(miss);
    return;
  }

  if(existing){
    // v126.75 — a Head Office WAIVER row (the 21-Jul outage class): items carry
    // {waived, note} and NO answers. The old banner read "Completed by undefined
    // at undefined" and the summary loop crashed on existing.answers[i]
    // ("Cannot read properties of undefined (reading '0')" — 6 occurrences,
    // 4 stores, the evening the waiver SQL ran). Render an honest waiver banner
    // and skip the per-item summary entirely.
    if (existing.waived) {
      var wbanner=document.createElement('div');
      wbanner.className='cl-submitted-banner';
      wbanner.innerHTML='<div style="font-size:32px;margin-bottom:8px">✅</div><h3>'+getChecklistName(clType)+' covered</h3>'+
        '<p>Marked as covered by '+escapeHtml(existing.completedBy||'Head Office')+'</p>'+
        (existing.waivedNote?'<p translate="no" style="margin-top:6px;font-size:12px;opacity:.85">'+escapeHtml(existing.waivedNote)+'</p>':'');
      body.appendChild(wbanner);
      if (viewingToday) {
        document.getElementById('clSubmitBtn').textContent='✅ Covered';
        document.getElementById('clSubmitBtn').disabled=true;
        document.getElementById('clSubmitBtn').className='cl-submit-btn submitted';
        document.getElementById('clProgress').textContent='';
      }
      return;
    }
    // Show submitted banner (both today-already-submitted and past-date-viewing cases use this branch)
    // v56: distinguish on-time vs late submissions
    var lateTag = existing.submittedLate
      ? '<div style="background:var(--warn-bg);color:var(--warn-deep);font-size:11px;font-weight:700;padding:3px 10px;border-radius:6px;display:inline-block;margin-top:6px">⚠ Submitted late (was due ' + escapeHtml(existing.lateForDate || '') + ')</div>'
      : '';
    var banner=document.createElement('div');
    banner.className='cl-submitted-banner';
    // v127.29 (27 Jul audit P1): staffName/submittedAt/comment/item text all
    // escapeHtml'd — ans.comment is STAFF-TYPED and was the one staff-writable
    // stored-XSS in the app (typed on the till, replayed to whoever views the
    // submitted checklist).
    banner.innerHTML='<div style="font-size:32px;margin-bottom:8px">✅</div><h3>'+getChecklistName(clType)+' submitted</h3><p>Completed by '+escapeHtml(existing.staffName||'—')+' at '+escapeHtml(existing.submittedAt||'—')+'</p><p style="margin-top:6px">'+(existing.score!=null?existing.score:'—')+'/'+(existing.total!=null?existing.total:'—')+' items answered Yes</p>' + lateTag;
    body.appendChild(banner);

    // Show read-only summary. v126.75 — answers can be ABSENT on a row not
    // written by the normal submit path; never crash the whole screen on it.
    // v126.78 — this MUST NOT be named clAnswers: that is the shared draft
    // global the EDITABLE branch below reads/writes, and a same-named `var`
    // here hoists over the whole function and shadows it — which blanked the
    // morning-checklist form at 18 stores on 22 Jul. Pinned by test.
    var subAnswers = (existing.answers && typeof existing.answers === 'object') ? existing.answers : {};
    var tmpl=getChecklistTemplate(clType);
    var allItems=[];tmpl.forEach(function(s){s.items.forEach(function(it){allItems.push({text:it,section:s.section});});});
    allItems.forEach(function(item,i){
      var ans=subAnswers[i];
      if(!ans)return;
      var row=document.createElement('div');
      row.style.cssText='background:var(--card);border-radius:10px;padding:10px 12px;margin-bottom:6px;display:flex;align-items:flex-start;gap:10px;box-shadow:0 1px 3px rgba(0,0,0,.05)';
      var dot=ans.answer==='yes'?'<span style="color:var(--ok-deep);font-size:14px;flex-shrink:0">✅</span>':'<span style="color:var(--danger-deep);font-size:14px;flex-shrink:0">❌</span>';
      row.innerHTML=dot+'<div><div style="font-size:12px;color:var(--text)">'+escapeHtml(item.text)+'</div>'+(ans.comment?'<div translate="no" style="font-size:11px;color:var(--sub);margin-top:3px">💬 '+escapeHtml(ans.comment)+'</div>':'')+'</div>';
      body.appendChild(row);
    });
    // Submit button handling only matters when the submit bar is actually visible (today)
    if (viewingToday) {
      document.getElementById('clSubmitBtn').textContent='✅ Submitted';
      document.getElementById('clSubmitBtn').disabled=true;
      document.getElementById('clSubmitBtn').className='cl-submit-btn submitted';
      document.getElementById('clProgress').textContent='';
    }
    return;
  }

  // Reset submit button
  document.getElementById('clSubmitBtn').textContent='Submit Checklist';
  document.getElementById('clSubmitBtn').disabled=false;
  document.getElementById('clSubmitBtn').className='cl-submit-btn';

  // Staff picker
  var staffRow=document.createElement('div');staffRow.className='cl-staff-row';
  var staffLabel=document.createElement('div');staffLabel.className='cl-staff-label';staffLabel.textContent='Who is completing this checklist?';
  var staffSel=document.createElement('select');staffSel.className='cl-staff-select';staffSel.id='clStaffPicker';
  var defOpt=document.createElement('option');defOpt.value='';defOpt.textContent='— Select your name —';staffSel.appendChild(defOpt);
  // v113.0 — roster = home staff + today's cover (see getStoreRoster in index.html)
  getStoreRoster(currentStore, dateStr).forEach(function(s){
    var opt=document.createElement('option');opt.value=s.id;opt.textContent=s.name;opt.setAttribute('translate','no');staffSel.appendChild(opt);   // v135.1: a name is never translated
  });
  staffSel.addEventListener('change',function(){clStaffId=this.value;updateClProgress();});
  if(clStaffId)staffSel.value=clStaffId;
  staffRow.appendChild(staffLabel);staffRow.appendChild(staffSel);
  body.appendChild(staffRow);

  // Items
  var tmpl=getChecklistTemplate(clType);
  var globalIdx=0;
  tmpl.forEach(function(section){
    var sec=document.createElement('div');sec.className='cl-section';
    var hdr=document.createElement('div');hdr.className='cl-section-hdr';hdr.textContent=section.section;
    sec.appendChild(hdr);
    section.items.forEach(function(itemText){
      var idx=globalIdx++;
      var item=document.createElement('div');item.className='cl-item';
      var top=document.createElement('div');top.className='cl-item-top';
      var num=document.createElement('div');num.className='cl-item-num';num.textContent=(idx+1);
      var txt=document.createElement('div');txt.className='cl-item-text';txt.textContent=itemText;
      top.appendChild(num);top.appendChild(txt);
      item.appendChild(top);
      // Yes/No buttons
      var actions=document.createElement('div');actions.className='cl-item-actions';
      var yesBtn=document.createElement('button');yesBtn.className='cl-answer-btn yes';yesBtn.textContent='✓ Yes';
      var noBtn=document.createElement('button');noBtn.className='cl-answer-btn no';noBtn.textContent='✗ No';
      var commentInput=document.createElement('textarea');
      commentInput.className='cl-comment-input';commentInput.placeholder='Add comment (optional)...';commentInput.rows=2;commentInput.style.display='none';
      // Restore state
      if(clAnswers[idx]){
        if(clAnswers[idx].answer==='yes')yesBtn.classList.add('active');
        if(clAnswers[idx].answer==='no'){noBtn.classList.add('active');commentInput.style.display='block';}
        commentInput.value=clAnswers[idx].comment||'';
      }
      (function(i,yb,nb,ci){
        yb.addEventListener('click',function(){
          clAnswers[i]={answer:'yes',comment:ci.value};
          yb.classList.add('active');nb.classList.remove('active');
          ci.style.display='none';updateClProgress();
        });
        nb.addEventListener('click',function(){
          clAnswers[i]={answer:'no',comment:ci.value};
          nb.classList.add('active');yb.classList.remove('active');
          ci.style.display='block';ci.focus();updateClProgress();
        });
        ci.addEventListener('input',function(){
          if(clAnswers[i])clAnswers[i].comment=ci.value;
        });
      })(idx,yesBtn,noBtn,commentInput);
      actions.appendChild(yesBtn);actions.appendChild(noBtn);
      item.appendChild(actions);item.appendChild(commentInput);
      sec.appendChild(item);
    });
    body.appendChild(sec);
  });
  updateClProgress();
}
screen.renderChecklistScreen = renderChecklistScreen;

async function submitChecklist(){
  var picker=document.getElementById('clStaffPicker');
  if(!picker||!picker.value){showToast('Please select your name first','error');return;}
  var staff=ALL_STAFF.find(function(x){return x.id==picker.value;});
  var tmpl=getChecklistTemplate(clType);
  var total=0;tmpl.forEach(function(s){total+=s.items.length;});
  var answered=Object.keys(clAnswers).length;
  if(answered<total){showToast('Please answer all items before submitting','error');return;}
  var score=Object.values(clAnswers).filter(function(a){return a.answer==='yes';}).length;
  var now=nowTime();
  // v56: when clLateFor is set, this is a late submission — record it against the DUE date
  // (not today), flag it, and preserve the due-date metadata. Submitted_at still = actual
  // submit time so compliance reports can compute "how late".
  var isLate = !!clLateFor;
  // v79.19: post-midnight closing checklist anchor.
  // Bug (a staff member, Dubai Hills, pilot day 1): a closing checklist submitted at
  // 00:15 Tuesday for Monday's close was dated to Tuesday, so Monday's
  // dashboard showed the close as missed and Tuesday gained a phantom
  // "submitted close" before the store had even opened.
  //
  // Same shape as the v79.8 midnight clock-out fix: anchor the record to
  // the actual shift day, not the calendar clock at submit time. This now
  // uses currentTradingDate() — the same helper that powers the dashboard's
  // default-day view, so the day shown in the header (Monday at 00:30 Tue)
  // matches the day records are filed under. localStorage key + DB date
  // both use recordDate; actual_submitted_on stays as todayDateStr() so
  // the discrepancy is auditable from the audit trail.
  var recordDate = isLate ? clLateFor : currentTradingDate();
  var anchoredToYesterday = (!isLate && recordDate !== todayDateStr());
  var record={
    store:currentStore,date:recordDate,type:clType,
    staffId:picker.value,staffName:staff?staff.name:picker.value,
    submittedAt:now,answers:clAnswers,score:score,total:total,
    submittedLate:isLate,
    lateForDate:isLate?clLateFor:null,
    actualSubmittedOn:todayDateStr()  // audit trail — when it was actually filed
  };
  // Save to localStorage (fast read for admin compliance view) — keyed by DUE date so
  // the history view for that day shows it as submitted.
  // v129.99 (Dar Al Salam, 9 Aug: "could not save .. storage full"): the
  // localStorage copy is a fast-read CACHE — a full device must NEVER block
  // the DB save below, which is the record. lsSetSafe evicts the avatar
  // cache + >90-day checklist records and retries; if it still can't store,
  // we warn and carry on to the DB save regardless.
  // v130.38 — synced:false until the SERVER has it. The local copy is written
  // before the DB call so answers survive a failure, but it used to be
  // indistinguishable from a real submission: after the error toast the
  // dashboard still read "✅ Submitted" off this record, so the store believed
  // it was filed and the compliance report — correctly — said it was missing.
  // (Meadows Town Center: no `open` row for 22 Aug, no `close` row for 21 Aug,
  // while the store's own Checks tab showed both as done.)
  // v65 made the save awaited so the error is timely; this makes the dashboard
  // honest about what actually reached the server.
  record.synced = false;
  if (!lsSetSafe(clStorageKey(currentStore,recordDate,clType),JSON.stringify(record))) {
    showToast('This device is out of storage — saving to the server anyway','info');
  }

  // v65: AWAIT the DB save instead of fire-and-forget. Previously the success
  // toast fired immediately while DB.save resolved 100-200ms later; if it
  // failed the error toast appeared after the user had moved on, and the
  // checklist showed as "submitted" (from localStorage) but the admin
  // dashboard never saw it. Multiple staff reports of "previous checklist
  // not recorded" — this is the fix.
  // Disable the submit button during the request so it can't be double-fired.
  var submitBtn = document.getElementById('clSubmitBtn');
  // v135.1: the English label, not whatever is on screen (with Arabic on, the screen holds the Arabic).
  var prevLabel = 'Submit Checklist';
  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Submitting…'; }

  if(typeof DB!=='undefined'){
    try {
      var saveResult = await saveChecklist({
        store: currentStore,
        date: recordDate,
        type: clType,
        items: {
          answers: clAnswers, score: score, total: total,
          staff_id: parseInt(picker.value) || null,
          staff_name: staff ? staff.name : picker.value,
          submitted_at: now,
          submitted_late: isLate,
          late_for_date: isLate ? clLateFor : null,
          actual_submitted_on: todayDateStr()
        },
        completed_by: staff ? staff.name : picker.value,
        completed_at: new Date().toISOString()
      });
      // v130.38 — the server has it now, so promote the local copy to synced.
      // Only from here is this record allowed to read as "submitted".
      // v130.39 — UNLESS it was QUEUED. Offline means the server does not have
      // it yet, so the record stays synced:false and the outbox promotes it
      // when it finally lands. Marking it synced here would recreate exactly
      // the lie v130.38 removed.
      if (!(saveResult && saveResult.queued)) {
        record.synced = true;
        lsSetSafe(clStorageKey(currentStore,recordDate,clType),JSON.stringify(record));
      }
    } catch(e) {
      console.error('[DB] Checklist save failed:', e && e.message);
      // Restore the button so the user can retry. Don't reset state — they
      // shouldn't have to refill the checklist. The localStorage copy stays,
      // so if they navigate away the answers are recoverable, but admin
      // dashboards (which read from DB only) won't see the entry.
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = prevLabel; }
      // v86.16: detect the cross-device duplicate-submission case (UNIQUE constraint
      // on checklists.(store, date, type) fires when a colleague already submitted
      // today on a different device — the localStorage existence-check at
      // renderChecklistScreen doesn't see across devices, so the form opens for
      // re-fill and save hits a 409). Translate the raw PostgREST error into
      // something the user can act on. The Bug 2B DB-first check should prevent
      // this from ever firing, but it's defensive — race conditions remain
      // (two staff start the form at the same time, one submits first).
      // (Bug reported by a staff member, Dubai - Wafi City, May 14 2026.)
      var errMsg = (e && e.message) ? String(e.message) : '';
      var isDuplicate = /\b409\b/.test(errMsg) || /duplicate key/i.test(errMsg) || /checklists_store_date_type/i.test(errMsg);
      if (isDuplicate) {
        showToast(
          'This ' + getChecklistName(clType).toLowerCase() +
          ' was already submitted today for ' + currentStore.replace('ELC - ','') +
          ' by a colleague on another device. Their submission is the record on file. ' +
          'Your answers are saved on this device — show them to the closing manager if anything needs adding.',
          'error'
        );
      } else {
        showToast('❌ Could not submit checklist to server — please check connection and tap Submit again. (Error: ' + ((e && e.message) || 'unknown') + ')', 'error');
}
      return;
    }
  }

  // v79.19: when an anchor rolled the record back to yesterday, tell the
  // user explicitly which day it was filed against. Otherwise the dashboard
  // (which shows TODAY by default) would not display the just-submitted
  // record and it'd look like the submit silently failed.
  var toastMsg = getChecklistName(clType) + (isLate ? ' submitted late ✓' : ' submitted ✓');
  if (anchoredToYesterday) {
    var yLabel = new Date(recordDate + 'T12:00:00').toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'});
    toastMsg = getChecklistName(clType) + ' submitted ✓ — filed against ' + yLabel;
  }
  // v130.39 — say what actually happened. A queued checklist is SAVED but NOT
  // yet filed, and telling the store it is submitted is the lie this whole
  // change exists to stop. It sends itself when the signal returns.
  if (typeof saveResult !== 'undefined' && saveResult && saveResult.queued) {
    showToast(getChecklistName(clType) + ' saved — no connection right now, it will send itself when the signal returns. You do not need to do it again.', 'info');
  } else {
    showToast(toastMsg,'success');
  }
  clearClPartialState(); // v65: clear partial-progress now that this checklist is committed
  clAnswers={};clStaffId=null;
  clLateFor=null;  // v56: clear so next open starts fresh
  // Return to dashboard so the user sees the updated "✅ Submitted" status
  renderChecklistDashboard();
}
screen.submitChecklist = submitChecklist;

// ── admin ─────────────────────────────────────────────
export const admin = {};

function initAdminChecklistView(){
  // Set default date range (last 30 days)
  var today=new Date();
  var d30=new Date();d30.setDate(d30.getDate()-30);
  var fromEl=document.getElementById('compFrom');
  var toEl=document.getElementById('compTo');
  if(fromEl&&!fromEl.value)fromEl.value=localDateStr(d30);
  if(toEl&&!toEl.value)toEl.value=todayDateStr();

  // Populate store filter
  var storeSel=document.getElementById('clAdminStore');
  if(storeSel&&storeSel.options.length<=1){
    getMyStores().forEach(function(st){
      var o=document.createElement('option');o.value=st;o.textContent=st;storeSel.appendChild(o);
    });
  }
  renderAdminChecklistView();
}
admin.initAdminChecklistView = initAdminChecklistView;

async function loadAdminChecklistRowsFromDB(){
  var from = document.getElementById('compFrom')?.value || '';
  var to   = document.getElementById('compTo')?.value   || '';
  if (typeof DB === 'undefined') { _adminChecklistCache = []; return; }
  try {
    var rows = await DB.checklists.list({ from: from, to: to });
    _adminChecklistCache = Array.isArray(rows) ? rows : [];
  } catch(e) {
    console.warn('[DB] Admin checklists load failed:', e.message);
    _adminChecklistCache = [];
  }
}
admin.loadAdminChecklistRowsFromDB = loadAdminChecklistRowsFromDB;

async function renderAdminChecklistView(){
  var el=document.getElementById('clAdminContent');
  if (el) el.innerHTML = '<div style="text-align:center;color:var(--sub);padding:40px 20px;font-size:13px">Loading…</div>';
  await loadAdminChecklistRowsFromDB();
  _renderAdminChecklistViewSync();
}
admin.renderAdminChecklistView = renderAdminChecklistView;

function _renderAdminChecklistViewSync(){
  var rows=getComplianceData();
  var el=document.getElementById('clAdminContent');
  if(!rows.length){
    el.innerHTML='<div style="text-align:center;padding:40px;color:var(--sub)"><div style="font-size:36px;margin-bottom:8px">✅</div>No submissions found for this date range and filters</div>';
    return;
  }

  // Summary stats
  var totalSubs=rows.length;
  var avgPct=Math.round(rows.reduce(function(s,r){return s+r.pct;},0)/totalSubs);
  var below90=rows.filter(function(r){return r.pct<90;}).length;
  var missed=rows.filter(function(r){return r.pct<100;});

  var html='<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px">';
  html+='<div style="background:var(--stores-ok-soft);border-radius:10px;padding:10px;text-align:center"><div style="font-size:20px;font-weight:800;color:var(--green)">'+totalSubs+'</div><div style="font-size:10px;color:var(--sub);text-transform:uppercase;font-weight:600">Submissions</div></div>';
  html+='<div style="background:'+(avgPct>=90?'var(--stores-ok-soft)':avgPct>=70?'var(--warn-bg)':'var(--danger-bg)')+'";border-radius:10px;padding:10px;text-align:center"><div style="font-size:20px;font-weight:800;color:'+(avgPct>=90?'var(--green)':avgPct>=70?'var(--stores-amber)':'var(--red)')+'">'+avgPct+'%</div><div style="font-size:10px;color:var(--sub);text-transform:uppercase;font-weight:600">Avg Completion</div></div>';
  html+='<div style="background:'+(below90===0?'var(--stores-ok-soft)':'var(--danger-bg)')+'";border-radius:10px;padding:10px;text-align:center"><div style="font-size:20px;font-weight:800;color:'+(below90===0?'var(--green)':'var(--red)')+'">'+below90+'</div><div style="font-size:10px;color:var(--sub);text-transform:uppercase;font-weight:600">Below 90%</div></div>';
  html+='</div>';

  // Submission list
  html+='<div style="border:1px solid var(--border);border-radius:10px;overflow:hidden">';
  html+='<table style="width:100%;border-collapse:collapse;font-size:12px">';
  html+='<thead><tr style="background:var(--surface-2)"><th style="padding:7px 10px;text-align:start;border-bottom:2px solid var(--border)">Store</th><th style="padding:7px 8px;border-bottom:2px solid var(--border)">Date</th><th style="padding:7px 8px;border-bottom:2px solid var(--border)">Type</th><th style="padding:7px 8px;border-bottom:2px solid var(--border)">Score</th><th style="padding:7px 8px;border-bottom:2px solid var(--border)">Missed</th><th style="padding:7px 8px;border-bottom:2px solid var(--border)">By</th></tr></thead><tbody>';
  rows.forEach(function(r){
    var pctColor=r.pct>=90?'var(--green)':r.pct>=70?'var(--stores-amber)':'var(--red)';
    var pctBg=r.pct>=90?'var(--ok-bg)':r.pct>=70?'var(--warn-bg)':'var(--danger-bg)';
    var shortStore=r.store.split(' - ').slice(-1)[0]; // last segment
    var missedHtml=r.missed.length?'<span style="color:var(--red);font-size:10px">'+r.missed.slice(0,2).join(', ')+(r.missed.length>2?' +'+( r.missed.length-2)+' more':'')+'</span>':'<span style="color:var(--green);font-size:10px">All done ✓</span>';
    html+='<tr style="border-bottom:1px solid var(--surface-3)">';
    html+='<td style="padding:6px 10px;font-weight:600">'+shortStore+'</td>';
    html+='<td style="padding:6px 8px;color:var(--sub)">'+escapeHtml(r.date)+'</td>';
    html+='<td style="padding:6px 8px">'+(r.type==='open'?'☀️':'🌙')+'</td>';
    html+='<td style="padding:6px 8px"><span style="background:'+pctBg+';color:'+pctColor+';padding:2px 8px;border-radius:6px;font-size:11px;font-weight:700">'+r.pct+'%</span></td>';
    html+='<td style="padding:6px 8px">'+missedHtml+'</td>';
    html+='<td translate="no" style="padding:6px 8px;color:var(--sub);font-size:11px">'+escapeHtml(r.submittedBy)+'</td>';   // v132.85: staff-supplied
    html+='</tr>';
  });
  html+='</tbody></table></div>';
  el.innerHTML=html;
}
admin._renderAdminChecklistViewSync = _renderAdminChecklistViewSync;

async function runChecklistAiAnalysis(){
  var resultDiv=document.getElementById('clAdminAiResult');
  var textDiv=document.getElementById('clAdminAiText');
  resultDiv.style.display='block';
  textDiv.innerHTML='<div class="ai-dot"></div><div class="ai-dot"></div><div class="ai-dot"></div> Analysing checklist compliance...';

  var rows=getComplianceData();
  if(!rows.length){textDiv.innerHTML='No data to analyse for this date range.';return;}

  // Summarise by store
  var byStore={};
  rows.forEach(function(r){
    if(!byStore[r.store])byStore[r.store]={subs:0,totalPct:0,missed:{}};
    byStore[r.store].subs++;
    byStore[r.store].totalPct+=r.pct;
    r.missed.forEach(function(m){byStore[r.store].missed[m]=(byStore[r.store].missed[m]||0)+1;});
  });
  var summary=Object.entries(byStore).map(function(e){
    var store=e[0],d=e[1];
    var avg=Math.round(d.totalPct/d.subs);
    var topMissed=Object.entries(d.missed).sort(function(a,b){return b[1]-a[1];}).slice(0,3).map(function(x){return x[0]+' (×'+x[1]+')';}).join(', ');
    return store+': avg '+avg+'% over '+d.subs+' submissions'+(topMissed?', most missed: '+topMissed:'');
  }).join('\n');

  var prompt='You are reviewing checklist compliance for ELC cosmetics stores.\n\n'+
    'Data summary:\n'+summary+'\n\n'+
    'Please provide:\n'+
    '1. Which stores have the lowest compliance and need attention\n'+
    '2. Which checklist items are most frequently missed across stores\n'+
    '3. Any patterns (e.g. worse on weekends, worse for closing vs opening)\n'+
    '4. Specific recommendations for the 2-3 stores that need the most improvement\n'+
    'Be concise, practical and direct.';

  try{
    var data=await DB.ai.rota({model:'claude-haiku-4-5-20251001',max_tokens:800,
        system:'You are a retail ops analyst reviewing store checklist compliance. Be concise and use bullet points.',
        messages:[{role:'user',content:prompt}]});
    var text=(data.content&&data.content[0]&&data.content[0].text)||'';
    textDiv.innerHTML=escapeHtml(text).replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>').replace(/^[•\-\*] /gm,'<br>• ').replace(/\n/g,'<br>');   // v136.8 (M3): model text escaped before formatting
  }catch(err){
    textDiv.innerHTML='❌ '+escapeHtml(err.message);
  }
}
admin.runChecklistAiAnalysis = runChecklistAiAnalysis;

// ── DEFAULT_CHECKLISTS — moved VERBATIM from index.html (v128.37, refactor R10) ──
// Sole-reader data constant (never written cross-bundle, so the "state
// stays declared in the HTML" law does not apply). Window-bound so any
// classic-script or cross-bundle bare reference keeps resolving.
var DEFAULT_CHECKLISTS = {
  open: [
    {section:'1 | Store Environment', items:[
      'Turn on AC, lighting, music, and all TV screens',
      'Morning clean — sweep/mop floors and high-traffic areas; remove rubbish',
      'Check cleaning products are stocked and accessible. If any equipment needs replacing, raise a Tazkera ticket.'
    ]},
    {section:'2 | Security & Safety', items:[
      'Check doors and windows for damage',
      'Check fire exits are clear and unlocked',
      'Sign attendance log',
      'Confirm safe balance matches previous night'
    ]},
    {section:'3 | Technology & Systems', items:[
      'Check ToyTalk for messages and outstanding actions',
      'Check e-mail, news, tasks and surveys on ToyTalk',
      'Log into CUBES and start day on POS — confirm system is working',
      'Test handheld devices, home delivery app, scanner, card payment terminals, and printers',
      'Check receipt paper — replace if needed',
      'Check delivery app — confirm no outstanding orders from previous day'
    ]},
    {section:'4 | Financial Setup', items:[
      'Count safe, petty cash, and opening float — report any variance immediately',
      'Visit bank if low on small denominations',
      'Complete DSR (Daily Sales Reconciliation) — log opening cash total',
      'Put cash in till and complete cash opening form',
      'Note till limit — drop cash to safe during shift when limit is reached'
    ]},
    {section:'5 | Morning Floor Walk', items:[
      'Confirm One Pager is actioned — displays, bulk stacks, and clip strips in place',
      'Ensure all demonstration lines are working — replace batteries if needed',
      'Fill empty shelves from stockroom',
      'Complete price sweep (missing SELs) and price integrity check (3 bays) — log both',
      'Action any overnight price changes from ToyTalk',
      'Verify promotional displays are current — check WOW items are correctly displayed'
    ]},
    {section:'6 | Team Readiness', items:[
      'Check day rota and confirm live link reflects today\'s schedule',
      'All staff in correct uniform with name badges',
      'Conduct team briefing — daily targets, promotions, and priorities',
      'Confirm staff schedules and break rotations',
      'Open store doors on time'
    ]}
  ],
  close: [
    {section:'1 | Financial Close', items:[
      'Confirm till limit drops were completed during shift — document any gaps. Max 2000 AED on site at any time.',
      'Count safe and petty cash — report any variances immediately. Max 500 AED in petty cash.',
      'Count till cash and record total',
      'Complete Z report on card payment device',
      'Prepare bank deposit, complete deposit slip (Excel file), and send to finance',
      'Log all cash into safe — till must be empty overnight',
      'Complete safe log book',
      'Log out of all systems, lock till, and secure all payment terminals'
    ]},
    {section:'2 | Store Presentation', items:[
      'Face up the store — tidy all shelves, straighten merchandise, reset promotional displays',
      'Ensure till area is clean and tidy — replenish gift wrapping station for next day',
      'Organise demonstration areas for tomorrow',
      'Clean and mop floor, empty all waste bins, clean customer touchpoints and surfaces'
    ]},
    {section:'3 | Stock Management', items:[
      'Fill empty spaces on shelves from stockroom — bring priority stock to sales floor',
      'Check top 10 bestselling items — flag any gaps to Line Manager',
      'Check and close down all delivery app orders',
      'Stockroom walk — aisles clear, stock stored safely, no product on floor'
    ]},
    {section:'4 | Technology & Equipment', items:[
      'Shut down all devices properly — do not leave on sleep mode. POS terminals should remain on overnight.',
      'Ensure all devices on charge overnight — including PDA and pricing gun',
      'Put rechargeable batteries on charge'
    ]},
    {section:'5 | Security & Safety', items:[
      'Check all areas — ensure all customers have left the store',
      'Confirm all staff have signed out and left the store',
      'Check all demo toys work and replace batteries',
      'Process any faulty stock (RFS procedure) — remove damaged products from sales floor',
      'Complete security walk-through',
      'Turn off lights, music system, and non-essential AC',
      'Lock all doors and windows — activate security system'
    ]},
    {section:'6 | Administrative Tasks', items:[
      'Check ToyTalk for messages and outstanding actions',
      'Write handover for morning shift and send EOD e-mail',
      'Complete incident logbook if required',
      'Update team communication board and file daily paperwork'
    ]}
  ]
};
if (typeof window !== "undefined") window.DEFAULT_CHECKLISTS = DEFAULT_CHECKLISTS;

// v130.39 — offline write queue for checklist submissions. Exported so the app
// can wire the replay triggers once on boot, exactly as Clock.initOutbox() does.
// Idempotent: safe to call on every checklist-screen render.
export { initChecklistOutbox, pendingCount as outboxPending, deadLetterCount as outboxDead, drain as drainChecklistOutbox } from './outbox.js';
